import React, { useEffect, useState, useRef } from "react";
import { Checkbox2 } from "@suralink/samurai/ui";
import { Button, Typography } from "@material-ui/core";
import { TextInput } from "@suralink/samurai/components";
import * as actions from "../../redux/actions";
import styles from "../../styles/SSOLogin.module.scss";
import { MfaBypassTokenDuration } from "../../enums/mfaBypassTokens";
import { connect } from "react-redux";

interface StateProps {
  userErrState: boolean;
  loginErrorState: {
    message: string;
    status: number;
  };
}

interface DispatchProps {
  clearErrorStateAction: () => any;
  toggleUserErrorAction: (val: boolean) => any;
}

interface ComponentProps {
  setPasswordMissing: any;
  passwordMissing: boolean;
  checkForm: any;
  useReCAPTCHA: boolean;
  handleSignIn: any;
  handleClickGetMfaRecoveryToken: any;
  setUserMessage: any;
  coBrandingEnabled: boolean;
  mfaError: string;
  setMfaError: any;
}

type Props = StateProps & DispatchProps & ComponentProps;

const Mfa: React.FC<Props> = ({
  userErrState,
  loginErrorState,
  setPasswordMissing,
  passwordMissing,
  clearErrorStateAction,
  toggleUserErrorAction,
  checkForm,
  useReCAPTCHA,
  handleSignIn,
  handleClickGetMfaRecoveryToken,
  setUserMessage,
  coBrandingEnabled,
  mfaError,
  setMfaError,
}) => {
  const [mfaCode, setMfaCode] = useState("");
  const [rememberMfaChecked, setRememberMfaChecked] = useState(false);
  const [rememberMfaDuration, setRememberMfaDuration] = useState(
    MfaBypassTokenDuration[0]
  );
  const mfaInput = useRef<HTMLInputElement>(null);

  const mfaInputIsValid =
    typeof mfaCode === "string" &&
    (mfaCode.length === 6 || mfaCode.length === 8);

  const checkMfaInput = () => {
    if (!mfaInputIsValid) {
      setMfaError("Please provide your two-factor passcode.");
      setPasswordMissing(true);
      toggleUserErrorAction(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (mfaError.includes("attempts")) {
      setMfaCode("");
    }
  }, [mfaError]);

  useEffect(() => {
    if (mfaCode && mfaError) {
      setMfaError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaCode]);

  useEffect(() => {
    if (mfaInput.current) {
      mfaInput.current.focus();
    }
  }, [mfaInput]);

  useEffect(() => {
    if (loginErrorState.message || passwordMissing) {
      setMfaCode("");
      mfaInput?.current?.focus();
    }
  }, [loginErrorState.message, passwordMissing]);

  function handleRememberMfaChecked() {
    const newCheckedState = !rememberMfaChecked;
    setRememberMfaChecked(newCheckedState);
    setRememberMfaDuration(
      newCheckedState ? MfaBypassTokenDuration[7] : MfaBypassTokenDuration[0]
    );
  }

  return (
    <React.Fragment>
      <TextInput
        type="text"
        ref={mfaInput}
        required
        showRequiredState={
          (passwordMissing || loginErrorState.status === 403) && userErrState
        }
        requiredText=""
        onChange={(e: any) => setMfaCode(e.target.value.trim())}
        id="mfacode"
        fullWidth
        error={mfaError}
        errorMessage={mfaError}
        value={mfaCode}
      />

      <input type="hidden" name="mfaCode" value={mfaCode} />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        style={{ width: "100%", margin: "1rem 0" }}
        onClick={(e: any) => {
          e.preventDefault();
          clearErrorStateAction();
          if (checkForm() && checkMfaInput()) {
            if (!useReCAPTCHA) {
              handleSignIn({
                e,
                reCaptchaToken: "",
                mfaCode,
                rememberMfaChecked,
                rememberMfaDuration,
              });
            } else {
              window.grecaptcha.ready(function () {
                window.grecaptcha
                  .execute(process.env.REACT_APP_RECAPTCHA_KEY, {
                    action: "submit",
                  })
                  .then(function (token: string) {
                    handleSignIn({
                      e,
                      reCaptchaToken: token,
                      mfaCode,
                      rememberMfaChecked,
                      rememberMfaDuration,
                    });
                  });
              });
            }
          }
        }}
      >
        Verify
      </Button>
      <div className={styles.rememberMfaSection}>
        <div className={styles.rememberMfaTextContainer}>
          <Checkbox2
            checked={rememberMfaChecked}
            onClick={() => handleRememberMfaChecked()}
          />
          <span>Remember me on this computer for 30 days</span>
        </div>
      </div>
      <Button
        onClick={handleClickGetMfaRecoveryToken}
        style={{ margin: "1rem auto 0 auto", width: "100%" }}
        variant="text"
      >
        I don't have access to my authenticator
      </Button>
      <hr
        style={{
          margin: `${coBrandingEnabled ? "36px auto" : "40px auto"}`,
        }}
      />
      <div style={{ textAlign: "center" }}>
        <Typography variant="body2">
          Need help?
          <a
            href="//suralink.zendesk.com/hc/en-us/sections/4423647909261-Two-Factor-Authentication"
            target="_blank"
            rel="noreferrer"
            style={{ marginLeft: "0.5rem" }}
          >
            Learn about two-factor authentication
          </a>
        </Typography>
      </div>
    </React.Fragment>
  );
};

const mstp = (state: InitialStateModel): StateProps => {
  const { userErrState, loginErrorState } = state;
  return {
    userErrState,
    loginErrorState,
  };
};

const matp = ((): DispatchProps => {
  const { toggleUserErrorAction, clearErrorStateAction } = actions;
  return {
    clearErrorStateAction,
    toggleUserErrorAction,
  };
})();

export default connect(mstp, matp)(Mfa);
